
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePublicCommunity } from '@/contexts/PublicCommunityContext';
import { useCommunityMember } from '@/contexts/CommunityMemberContext';
import { useAuth } from '@/contexts/AuthContext';
import { PrivateAccessMessage } from './community-access/PrivateAccessMessage';
import { BlockedAccessMessage } from './community-access/BlockedAccessMessage';
import { JoinPrompt } from './community-access/JoinPrompt';
import { Loader2 } from 'lucide-react';
import { PATHS } from '@/routes/routePaths';

interface CommunityAccessCheckProps {
  children: React.ReactNode;
}

export function CommunityAccessCheck({ children }: CommunityAccessCheckProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, isProfileComplete, userProfile } = useAuth();
  const { currentCommunity } = usePublicCommunity();
  const { 
    isUserInCommunity, 
    isCheckingAccess, 
    error,
    memberStatus,
    joinCommunity 
  } = useCommunityMember();

  const adminContact = currentCommunity?.admin_contact_email;

  useEffect(() => {
    // Don't run navigation logic while data is loading
    if (loading || isCheckingAccess) return;

    const handleNavigation = () => {
      // Create a full path including hash for redirects
      const fullPath = location.pathname + location.search + location.hash;
      
      // Handle private community access
      if (!user && currentCommunity?.privacy_type === 'private') {
        navigate(PATHS.AUTH, {
          state: {
            message: 'This is a private community. Please log in to continue.',
            returnTo: fullPath // Include hash in returnTo
          },
          replace: true
        });
        return;
      }

      // Redirect to complete registration if:
      // 1. User is logged in
      // 2. User either has incomplete profile or is not in community
      // 3. Not already on the complete-registration page
      if (user && 
          ((!isProfileComplete) || !isUserInCommunity) && 
          location.pathname !== PATHS.COMPLETE_REGISTRATION) {
        
        // Use URL parameter instead of sessionStorage to preserve the original path
        navigate(`${PATHS.COMPLETE_REGISTRATION}?returnTo=${encodeURIComponent(fullPath)}`, { replace: true });
        return;
      }

      // Redirect from registration page if not needed
      if (location.pathname === PATHS.COMPLETE_REGISTRATION) {
        if (!user) {
          navigate(PATHS.HOME, { replace: true });
        } else if (isProfileComplete && isUserInCommunity) {
          // Get returnTo from URL param instead of sessionStorage
          const searchParams = new URLSearchParams(location.search);
          const returnPath = searchParams.get('returnTo');
          
          if (returnPath) {
            navigate(returnPath, { replace: true });
          } else {
            navigate(PATHS.HOME, { replace: true });
          }
        }
      }
    };

    handleNavigation();
  }, [
    user,
    isProfileComplete,
    isUserInCommunity,
    loading,
    isCheckingAccess,
    location.pathname,
    location.search,
    location.hash,
    currentCommunity?.privacy_type,
    navigate,
    userProfile
  ]);

  // Show loading state while checking auth and access
  if (loading || isCheckingAccess) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    console.error('Community access error:', error);
    return null;
  }

  // Check if user is blocked from the community
  if (user && isUserInCommunity && memberStatus === 'BLOCKED') {
    return (
      <BlockedAccessMessage 
        communityName={currentCommunity?.name || 'Community'} 
        adminContact={adminContact} 
      />
    );
  }

  // Only show join prompt if:
  // 1. User is logged in 
  // 2. User has a complete profile
  // 3. User is not in the community already
  if (user && !isUserInCommunity && isProfileComplete) {
    if (currentCommunity?.privacy_type === 'private') {
      return (
        <PrivateAccessMessage 
          communityName={currentCommunity.name} 
          adminContact={adminContact} 
        />
      );
    }

    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-72px)] px-4 py-8 bg-white">
        <JoinPrompt
          communityName={currentCommunity?.name || ''}
          onJoin={joinCommunity}
          isJoining={isCheckingAccess}
        />
      </div>
    );
  }

  return <>{children}</>;
}
